
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
} from "@/components/ui/navigation-menu";
import { Menu, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import LanguageSwitcher from './LanguageSwitcher';
import { usePortalResolver } from '@/hooks/usePortalResolver';

const Navbar = () => {
  const { user, activeRole, isLoading, getUserType, signOut } = useAuth();
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { preferredPortal, resolvePortal } = usePortalResolver();

// Role checks removed for faster navigation; we resolve portal in background

  const handleDashboardRedirect = () => {
    if (!user || isLoading) return; // Exit if still loading or no user

    const portal = preferredPortal ?? 'unknown';

    if (activeRole === 'admin') {
      navigate('/admin/dashboard');
      return;
    }
    if (activeRole === 'practitioner' || portal === 'practitioner') {
      navigate('/practitioner-dashboard');
      return;
    }
    if (activeRole === 'client' || portal === 'client') {
      navigate('/client-dashboard');
      return;
    }

    // Fallback fast, resolve in background
    void resolvePortal();
    navigate('/client-dashboard');
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isPathActive = (path: string) => {
    return location.pathname === path;
  };

  const navigationLinks = [
    { path: '/booking-choice', label: t('bookAppointment') },
    { path: '/certifications', label: t('certifications') },
    { path: '/about-us', label: t('aboutUs') },
    { path: '/news-events', label: t('newsEvents') },
  ];

  const renderNavLinks = () => (
    navigationLinks.map((link) => (
      <NavigationMenuItem key={link.path}>
        <Link to={link.path} className={cn(
          "text-gray-600 hover:text-amber-600 px-2 lg:px-3 py-2 text-sm lg:text-base font-medium whitespace-nowrap",
          isPathActive(link.path) && "text-amber-600 font-semibold"
        )}>
          {link.label}
        </Link>
      </NavigationMenuItem>
    ))
  );

  const renderMobileMenuItems = () => (
    navigationLinks.map((link) => (
      <SheetClose key={link.path} asChild>
        <Link 
          to={link.path} 
          className={cn(
            "block py-3 px-4 text-lg border-b border-gray-100 transition-colors",
            isPathActive(link.path) 
              ? "text-amber-600 font-semibold" 
              : "text-gray-700 hover:text-amber-600"
          )}
        >
          {link.label}
        </Link>
      </SheetClose>
    ))
  );

  return (
    <nav className="bg-white bg-opacity-95 border-b border-gray-200 px-3 sm:px-6 py-3 fixed w-full z-[80]" role="navigation" aria-label="Main navigation">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Link to="/" className="flex items-center space-x-2" aria-label="Resonow homepage">
            <img
              src="/lovable-uploads/ba1e90a0-0271-4ea2-80f0-40d8acccb492.png"
              alt="Resonow - Professional healing platform logo"
              className="h-6 w-6"
            />
            <span className="text-xl font-semibold text-amber-600">Resonow</span>
          </Link>
        </div>

        {/* Desktop Navigation */}
        {!isMobile && (
          <div className="flex items-center space-x-4 lg:space-x-6">
            <NavigationMenu>
              <NavigationMenuList className="space-x-2 lg:space-x-4">
                {renderNavLinks()}
              </NavigationMenuList>
            </NavigationMenu>

            <div className="flex items-center space-x-3">
              <LanguageSwitcher />
              
              {!user && (
                <Button
                  onClick={() => navigate('/client-auth')}
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-600 hover:bg-amber-50 font-medium text-sm px-4 py-2 min-h-[40px] min-w-[100px]"
                  aria-label="Login or sign up to Resonow"
                >
                  {t('loginSignUp')}
                </Button>
              )}

              {user && (
                <div className="flex items-center space-x-3">
                  <Button
                    onClick={handleDashboardRedirect}
                    variant="outline" 
                    size="sm"
                    className="border-amber-600 text-amber-600 hover:bg-amber-50 font-medium text-sm px-4 py-2 min-h-[40px]"
                    aria-label="Go to my dashboard"
                  >
                    {t('myDashboard')}
                  </Button>
                  <Button
                    onClick={handleSignOut}
                    variant="ghost"
                    size="sm"
                    className="text-gray-600 hover:text-red-600 font-medium text-sm px-3 py-2 min-h-[40px]"
                    aria-label="Logout from Resonow"
                  >
                    <LogOut className="h-4 w-4 mr-1" />
                    Logout
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Navigation with Sheet/Drawer */}
        {isMobile && (
          <div className="flex items-center space-x-2">
            {!user && (
              <Button
                onClick={() => navigate('/client-auth')}
                variant="outline"
                size="sm"
                className="border-amber-600 text-amber-600 hover:bg-amber-50 font-medium text-sm px-3 py-2 min-h-[44px] min-w-[80px]"
              >
                {t('common:login')}
              </Button>
            )}

            {user && (
              <div className="flex items-center space-x-2">
                <Button
                  onClick={handleDashboardRedirect}
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-600 hover:bg-amber-50 font-medium text-sm px-3 py-2 min-h-[44px]"
                >
                  Dashboard
                </Button>
                <Button
                  onClick={handleSignOut}
                  variant="ghost"
                  size="icon"
                  className="text-gray-600 hover:text-red-600 min-h-[44px] min-w-[44px]"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px] p-0" aria-label="Open mobile navigation menu">
                  <Menu className="h-6 w-6 text-amber-700" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px]" role="dialog" aria-labelledby="mobile-menu-title">
                <SheetHeader className="mb-6">
                  <SheetTitle id="mobile-menu-title" className="text-amber-700 text-lg">{t('menu')}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col space-y-2">
                  {renderMobileMenuItems()}
                  
                  <div className="border-t border-gray-200 my-4"></div>
                  
                  <div className="mb-4">
                    <LanguageSwitcher />
                  </div>
                  
                  {user ? (
                    <div className="space-y-3">
                      <SheetClose asChild>
                        <Button
                          onClick={handleDashboardRedirect}
                          variant="ghost"
                          className="justify-start text-left text-amber-700 w-full h-12 px-4 font-medium"
                        >
                          {t('myDashboard')}
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button
                          onClick={handleSignOut}
                          variant="ghost"
                          className="justify-start text-left text-red-600 hover:text-red-700 w-full h-12 px-4 font-medium"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Logout
                        </Button>
                      </SheetClose>
                    </div>
                  ) : (
                    <SheetClose asChild>
                      <Button
                        onClick={() => navigate('/client-auth')}
                        variant="ghost"
                        className="justify-start text-left text-amber-700 w-full h-12 px-4 font-medium"
                      >
                        {t('loginSignUp')}
                      </Button>
                    </SheetClose>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
